import { RequestTypeKey } from 'app/modules/user/createTicket/contextualDetails/types';

/**
 * Shared view-model types for rendering the new ticket shape on the seller
 * dashboard and admin views.
 *
 * These describe the *rendered* shape of a ticket on those surfaces. They
 * deliberately reuse the create/add-customer request-type mapping
 * (`RequestTypeKey`) instead of redefining it, so contextual-details variant
 * selection stays in a single place.
 */

/**
 * The viewer's relationship to the ticket being rendered, used to gate
 * view-surface behaviour (e.g. a viewer appending a customer creates a linked
 * parent ticket rather than mutating the creator's ticket). Resolved by
 * `roleResolver` from the viewer's admin status and whether they created the
 * ticket being viewed.
 *
 * - `creator` — the signed-in user created the ticket being viewed.
 * - `admin`   — Admin/Super Admin.
 * - `viewer`  — everyone else.
 */
export enum ViewerRole {
    Creator = 'creator',
    Admin = 'admin',
    Viewer = 'viewer',
}

/**
 * Contextual-details variant to render on the Request Details tab. Reuses the
 * wizard's `RequestTypeKey` for the typed variants; `Other` collapses every
 * remaining type into the hidden/no-card case.
 */
export type RequestTypeVariant = RequestTypeKey | 'other';

/** Minimal customer shape needed to render a multi-customer summary. */
export interface CustomerSummaryItem {
    /** Display name shown first in the `[first name] +N more` summary. */
    accountName?: string;
}

/**
 * Inputs required to resolve a viewer's role for a specific ticket. Callers
 * derive `isAdmin` / `isSuperAdmin` from the existing `app/utils/authUtilities`
 * helpers so this module never decodes tokens itself.
 */
export interface ViewerRoleContext {
    isAdmin: boolean;
    isSuperAdmin: boolean;
    /** Object id (oid) of the signed-in user. */
    currentUserId?: string;
    /** Object id (oid) of the ticket's creator. */
    ticketCreatorId?: string;
}
